import { FC } from 'react';
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { toJalaliDateOrNull } from 'core/utils/date';
import { ToFa, digitSperator } from 'core/utils/decimalExtentions';

/**
 * پالت مشترک نمودارهای پیشخوان (تم روشن پی‌ام‌ایکس).
 */
export const CHART_COLORS = {
    blue: '#3364e4',
    indigo: '#333dc2',
    green: '#12b76a',
    red: '#f04438',
    amber: '#f79009',
    purple: '#7a5af8',
    cyan: '#06aed4',
    grid: '#eaeffb',
    axis: '#8c97b2',
};

export const CHART_HEIGHT = 300;

export interface ChartPoint {
    dateTime: string;
    value: number;
}

export interface ChartSeries {
    key: string;
    name: string;
    color: string;
}

export interface MergedPoint {
    /** برچسب کوتاه محور افقی، مثل ۰۸/۰۴ */
    label: string;
    /** تاریخ کامل شمسی، برای مرتب‌سازی و تولتیپ */
    fullDate: string;
    [seriesKey: string]: number | string;
}

/** عدد فشرده با پسوند فارسی (هزار / میلیون / میلیارد) — مثل فرمترهای قبلی محور Y. */
export const compactNumber = (value: number): string => {
    const abs = Math.abs(value);
    const trim = (v: number) => Number(v.toFixed(1)).toString();
    if (abs >= 1_000_000_000) return `${trim(value / 1_000_000_000)} میلیارد`;
    if (abs >= 1_000_000) return `${trim(value / 1_000_000)} میلیون`;
    if (abs >= 1_000) return `${trim(value / 1_000)} هزار`;
    return trim(value);
};

/** عدد با جداکننده هزارگان و ارقام فارسی. */
export const faNumber = (value: number | null | undefined): string => {
    if (value === null || value === undefined || Number.isNaN(value)) return '—';
    return ToFa(digitSperator(Number(value)) ?? 0);
};

/** جمع یک سری در کل بازه. */
export const sumOf = (data: MergedPoint[], key: string): number =>
    data.reduce((total, row) => total + (Number(row[key]) || 0), 0);

/**
 * بک‌اند برای هر سری یک آرایه جدا برمی‌گرداند؛ ری‌چارتز به ازای هر تاریخ یک ردیف می‌خواهد.
 * تاریخ‌های نامعتبر/سنتینل (0001-01-01) حذف می‌شوند — همان رفتار قبلی نمودارها.
 */
export const mergeByDate = (series: { key: string; data?: ChartPoint[] }[]): MergedPoint[] => {
    const map: { [fullDate: string]: MergedPoint } = {};

    series.forEach(({ key, data }) => {
        (data ?? []).forEach((point) => {
            if (!point) return;
            const fullDate = toJalaliDateOrNull(point.dateTime);
            if (!fullDate) return;
            if (!map[fullDate]) {
                map[fullDate] = { fullDate, label: fullDate.slice(fullDate.indexOf('/') + 1) };
            }
            map[fullDate][key] = point.value;
        });
    });

    const keys = series.map((s) => s.key);

    return Object.values(map)
        .sort((a, b) => (a.fullDate < b.fullDate ? -1 : a.fullDate > b.fullDate ? 1 : 0))
        .map((row) => {
            keys.forEach((key) => {
                if (row[key] === null || row[key] === undefined) row[key] = 0;
            });
            return row;
        });
};

/** تولتیپ راست‌چین با کارت روشن، نقطه رنگی و عدد جداشده. */
const ChartTooltip: FC<any> = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const fullDate = payload[0]?.payload?.fullDate;

    return (
        <div className="pmx-chart-tooltip" dir="rtl">
            <p className="date">{ToFa(fullDate ?? '')}</p>
            {payload.map((entry: any) => (
                <div className="row" key={entry.dataKey}>
                    <span className="dot" style={{ background: entry.color }} />
                    <span className="name">{entry.name}</span>
                    <span className="value">{faNumber(Number(entry.value))}</span>
                </div>
            ))}
        </div>
    );
};

const axisProps = {
    stroke: CHART_COLORS.axis,
    tick: { fill: CHART_COLORS.axis, fontSize: 11 },
    tickLine: false,
};

const renderXAxis = () => (
    <XAxis
        dataKey="label"
        {...axisProps}
        axisLine={{ stroke: CHART_COLORS.grid }}
        tickFormatter={(value: string) => ToFa(value ?? '')}
        minTickGap={16}
    />
);

const renderYAxis = () => (
    <YAxis
        orientation="right"
        width={70}
        {...axisProps}
        axisLine={false}
        tickFormatter={(value: number) => ToFa(compactNumber(value))}
    />
);

const legendStyle = { fontSize: 12, fontWeight: 700, paddingTop: 8 };

interface SeriesChartProps {
    data: MergedPoint[];
    series: ChartSeries[];
    height?: number;
}

export const AreaSeriesChart: FC<SeriesChartProps> = ({ data, series, height = CHART_HEIGHT }) => (
    <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ top: 8, right: 4, left: 4, bottom: 0 }}>
            <defs>
                {series.map((s) => (
                    <linearGradient key={s.key} id={`pmx-grad-${s.key}-${s.color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={s.color} stopOpacity={0.28} />
                        <stop offset="100%" stopColor={s.color} stopOpacity={0.01} />
                    </linearGradient>
                ))}
            </defs>
            <CartesianGrid strokeDasharray="4 4" vertical={false} stroke={CHART_COLORS.grid} />
            {renderXAxis()}
            {renderYAxis()}
            <Tooltip content={<ChartTooltip />} cursor={{ stroke: CHART_COLORS.grid, strokeWidth: 2 }} />
            <Legend iconType="circle" wrapperStyle={legendStyle} />
            {series.map((s) => (
                <Area
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={s.name}
                    stroke={s.color}
                    strokeWidth={2.5}
                    fill={`url(#pmx-grad-${s.key}-${s.color.replace('#', '')})`}
                    dot={false}
                    activeDot={{ r: 4, strokeWidth: 2, stroke: '#ffffff' }}
                />
            ))}
        </AreaChart>
    </ResponsiveContainer>
);

export const BarSeriesChart: FC<SeriesChartProps> = ({ data, series, height = CHART_HEIGHT }) => (
    <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 8, right: 4, left: 4, bottom: 0 }}>
            <CartesianGrid strokeDasharray="4 4" vertical={false} stroke={CHART_COLORS.grid} />
            {renderXAxis()}
            {renderYAxis()}
            <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(51, 100, 228, 0.06)' }} />
            <Legend iconType="circle" wrapperStyle={legendStyle} />
            {series.map((s) => (
                <Bar key={s.key} dataKey={s.key} name={s.name} fill={s.color} radius={[6, 6, 0, 0]} maxBarSize={34} />
            ))}
        </BarChart>
    </ResponsiveContainer>
);
